import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Box, Text, Flex, Icon, Spinner, VStack } from '@chakra-ui/react';
import { Warning } from 'phosphor-react';
import { PasswordForm, PublicDossieView } from './components';
import { useDossie } from '../../hooks/useDossie';
import {
  getTrackingHeaders,
  getTrackingHeadersSync,
  ITrackingHeaders,
} from '../../services/tracking';
import {
  IPublicFaturamentoItem,
  IPublicFolhaData,
  IPublicEmpresaDetalhes,
} from '../modulesHome/dossieEmpresa/utils/dossieTypes';

interface DossieData {
  faturamento: IPublicFaturamentoItem[];
  folha: IPublicFolhaData | null;
  empresa: IPublicEmpresaDetalhes | null;
}

// Ano default: ano atual - 1
const DEFAULT_ANO_FOLHA = new Date().getFullYear() - 1;

export function DossiePublicoPage() {
  const { hashedLink } = useParams<{ hashedLink: string }>();
  const [searchParams] = useSearchParams();
  const { accessByTemporaryLink } = useDossie();

  // Senha da URL (parâmetro 'p')
  const urlPassword = searchParams.get('p');

  // Ref para controlar se já tentou auto-login
  const autoLoginAttempted = useRef(false);

  // Headers de tracking (geolocalização + dados do dispositivo)
  // Inicializa com dados básicos de forma síncrona para garantir que estejam disponíveis no auto-login
  const [trackingHeaders, setTrackingHeaders] = useState<ITrackingHeaders>(() =>
    getTrackingHeadersSync(),
  );
  const trackingCollected = useRef(false);

  // Estado de autenticação
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingFolha, setIsLoadingFolha] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isAutoLogging, setIsAutoLogging] = useState(!!urlPassword);

  // Ano selecionado para folha de pagamento
  const [anoFolha, setAnoFolha] = useState(DEFAULT_ANO_FOLHA);

  // Senha salva para futuras requisições
  const [savedPassword, setSavedPassword] = useState<string | null>(null);

  // Datas salvas para re-fetch da folha
  const [savedDates, setSavedDates] = useState<{ dateStart: string; dateEnd: string } | null>(null);

  // Dados do dossiê
  const [dossieData, setDossieData] = useState<DossieData>({
    faturamento: [],
    folha: null,
    empresa: null,
  });

  // Datas padrão (mês anterior)
  const getDefaultDates = useCallback(() => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth(), 0);
    return {
      dateStart: `${firstDay.getFullYear()}-${String(firstDay.getMonth() + 1).padStart(2, '0')}-01`,
      dateEnd: `${lastDay.getFullYear()}-${String(lastDay.getMonth() + 1).padStart(2, '0')}-${lastDay.getDate()}`,
    };
  }, []);

  // Função para buscar todos os dados (primeira carga e filtro de período)
  const fetchData = useCallback(
    async (password: string, dateStart: string, dateEnd: string, ano: number) => {
      if (!hashedLink) return;

      setIsLoading(true);
      setError(null);

      try {
        const response = await accessByTemporaryLink(
          {
            hashed_link: hashedLink,
            password,
            date_start: dateStart,
            date_end: dateEnd,
            ano_folha_pagamento: ano,
          },
          trackingHeaders,
        );

        // Extrai dados do array de resposta conforme documentação da API:
        // [0] impostos - Dados de impostos
        // [1] faturamento - Array de faturamento
        // [2] folha_pagamento - Objeto com meses, totais, qnt_vidas, etc
        // [3] detalhes_empresa - Dados cadastrais (razao_social, cnpj)
        const [, faturamentoArray, folhaData, empresaDetalhes] = response.data;

        setDossieData({
          faturamento: Array.isArray(faturamentoArray) ? faturamentoArray : [],
          folha: folhaData || null,
          empresa: empresaDetalhes || null,
        });

        // Salva credenciais para futuras requisições
        setSavedPassword(password);
        setSavedDates({ dateStart, dateEnd });
        setIsAuthenticated(true);
      } catch (err: any) {
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail;

        if (status === 401) {
          if (detail?.includes('expirado')) {
            setError('Este link expirou. Solicite um novo link ao responsável.');
          } else {
            setError('Senha incorreta. Verifique e tente novamente.');
          }
        } else if (status === 404) {
          setError('Link não encontrado. Verifique se o endereço está correto.');
        } else {
          setError('Erro ao acessar os dados. Tente novamente mais tarde.');
        }
      } finally {
        setIsLoading(false);
      }
    },
    [hashedLink, accessByTemporaryLink, trackingHeaders],
  );

  // Função para buscar apenas a folha (quando muda o ano)
  const fetchFolhaOnly = useCallback(
    async (ano: number) => {
      if (!hashedLink || !savedPassword || !savedDates) return;

      setIsLoadingFolha(true);

      try {
        const response = await accessByTemporaryLink(
          {
            hashed_link: hashedLink,
            password: savedPassword,
            date_start: savedDates.dateStart,
            date_end: savedDates.dateEnd,
            ano_folha_pagamento: ano,
          },
          trackingHeaders,
        );

        const [, , folhaData] = response.data;

        setDossieData(prev => ({
          ...prev,
          folha: folhaData || null,
        }));
      } catch (err) {
        console.error('Erro ao buscar folha:', err); // eslint-disable-line no-console
      } finally {
        setIsLoadingFolha(false);
      }
    },
    [hashedLink, savedPassword, savedDates, accessByTemporaryLink, trackingHeaders],
  );

  // Handler do formulário de senha (primeira autenticação)
  const handlePasswordSubmit = useCallback(
    async (password: string) => {
      const dates = getDefaultDates();
      await fetchData(password, dates.dateStart, dates.dateEnd, anoFolha);
    },
    [fetchData, getDefaultDates, anoFolha],
  );

  // Coleta headers de tracking (geolocalização + dados do dispositivo)
  useEffect(() => {
    if (!trackingCollected.current) {
      trackingCollected.current = true;
      // Coleta dados básicos imediatamente
      setTrackingHeaders(getTrackingHeadersSync());
      // Coleta geolocalização de forma assíncrona (solicita permissão)
      getTrackingHeaders().then(headers => {
        setTrackingHeaders(headers);
      });
    }
  }, []);

  // Auto-login quando há senha na URL
  useEffect(() => {
    if (urlPassword && hashedLink && !autoLoginAttempted.current) {
      autoLoginAttempted.current = true;
      handlePasswordSubmit(urlPassword).finally(() => {
        setIsAutoLogging(false);
      });
    }
  }, [urlPassword, hashedLink, handlePasswordSubmit]);

  // Handler de mudança de filtro de período (usa senha salva)
  const handleFilterChange = useCallback(
    async (dateStart: string, dateEnd: string) => {
      if (savedPassword) {
        setSavedDates({ dateStart, dateEnd });
        await fetchData(savedPassword, dateStart, dateEnd, anoFolha);
      }
    },
    [savedPassword, fetchData, anoFolha],
  );

  // Handler de mudança do ano da folha
  const handleAnoFolhaChange = useCallback(
    async (novoAno: number) => {
      setAnoFolha(novoAno);
      await fetchFolhaOnly(novoAno);
    },
    [fetchFolhaOnly],
  );

  // Memoriza os dados para evitar re-renders desnecessários
  const memoizedData = useMemo(() => dossieData, [dossieData]);

  // Se não tem hashedLink na URL
  if (!hashedLink) {
    return (
      <Flex minH="100vh" align="center" justify="center" bg="gray.50" p={4}>
        <Box bg="white" p={8} borderRadius="xl" boxShadow="lg" textAlign="center">
          <Icon as={Warning} boxSize={12} color="orange.500" mb={4} />
          <Text fontSize="lg" fontWeight="bold" color="gray.800" mb={2}>
            Link inválido
          </Text>
          <Text color="gray.500">O link que você está tentando acessar não é válido.</Text>
        </Box>
      </Flex>
    );
  }

  // Tela de loading durante auto-login
  if (isAutoLogging) {
    return (
      <Flex minH="100vh" align="center" justify="center" bg="gray.50" p={4}>
        <VStack spacing={4}>
          <Spinner size="xl" color="blue.500" thickness="4px" />
          <Text color="gray.600" fontSize="lg">
            Carregando dossiê...
          </Text>
        </VStack>
      </Flex>
    );
  }

  // Tela de senha (não autenticado e sem senha na URL)
  if (!isAuthenticated) {
    return <PasswordForm onSubmit={handlePasswordSubmit} isLoading={isLoading} error={error} />;
  }

  // Tela do dossiê (autenticado)
  return (
    <PublicDossieView
      faturamento={memoizedData.faturamento}
      folha={memoizedData.folha}
      empresa={memoizedData.empresa}
      onFilterChange={handleFilterChange}
      isLoading={isLoading}
      anoFolha={anoFolha}
      onAnoFolhaChange={handleAnoFolhaChange}
      isLoadingFolha={isLoadingFolha}
    />
  );
}
